// ===========================================
// Student Progress Summary Cards Component
// ===========================================
// Display key student progress metrics using StatWidget cards

import { Grid } from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  CheckCircle as CheckIcon,
  TrendingUp as TrendingUpIcon,
  MusicNote as MusicIcon,
  LocalFireDepartment as StreakIcon,
} from '@mui/icons-material';

import { StudentProgressSummary } from '../../api/reports.api';
import { StatWidget } from '../dashboard/StatWidget';
import { formatPercentage } from '../../hooks/useReports';

// ===========================================
// TYPES
// ===========================================

interface StudentProgressSummaryCardsProps {
  summary: StudentProgressSummary;
  loading?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function StudentProgressSummaryCards({
  summary,
  loading = false,
}: StudentProgressSummaryCardsProps) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {/* Total Students */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Total Students"
          value={summary.totalStudents}
          icon={<PeopleIcon />}
          color="primary"
          subtitle={`${summary.totalLessonsDelivered} lessons`}
          loading={loading}
        />
      </Grid>

      {/* Average Lessons Attended */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Avg Lessons"
          value={summary.averageLessonsAttended.toFixed(1)}
          icon={<SchoolIcon />}
          color="info"
          subtitle="per student"
          loading={loading}
        />
      </Grid>

      {/* Average Attendance Rate */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Attendance Rate"
          value={formatPercentage(summary.averageAttendanceRate)}
          icon={<CheckIcon />}
          color="success"
          subtitle="average"
          loading={loading}
        />
      </Grid>

      {/* Average Progress Rate */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Progress Rate"
          value={formatPercentage(summary.averageProgressRate)}
          icon={<TrendingUpIcon />}
          color="secondary"
          subtitle="pieces completed"
          loading={loading}
        />
      </Grid>

      {/* Total Pieces Completed */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Pieces Completed"
          value={summary.totalPiecesCompleted}
          icon={<MusicIcon />}
          color="primary"
          loading={loading}
        />
      </Grid>

      {/* Average Streak */}
      <Grid item xs={12} sm={6} md={2}>
        <StatWidget
          title="Avg Streak"
          value={summary.averageStreak.toFixed(0)}
          icon={<StreakIcon />}
          color="error"
          subtitle="days"
          loading={loading}
        />
      </Grid>
    </Grid>
  );
}
